/**
 * Plattformverwaltung der Workspaces. Die einzige tenantübergreifende Fläche — sie verwaltet
 * Workspaces und zeigt außer den Administratoren, die die Plattform einlädt, keinerlei
 * Kundendaten. Serverseitig PlatformAdmin-only; die Sichtbarkeit hier ist nur UX.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  SupportStartDialog,
  useDirectSupportSwitch,
  type SupportTarget,
} from '@/features/support-session';
import { Button, EmptyState, PageHeader, Pagination } from '@/shared/components';
import { toApiError, useApi, type CreateWorkspaceRequest } from '@/shared/api';
import { workspaceKeys } from './queries';
import { useAdminInvitationResend } from './useAdminInvitationResend';
import { WorkspaceCreateDialog } from './WorkspaceCreateDialog';
import { WorkspacesList } from './WorkspacesList';

const PAGE_SIZE = 25;

export function WorkspacesPage() {
  const { t } = useTranslation('workspaces');
  const api = useApi();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [supportTarget, setSupportTarget] = useState<SupportTarget | null>(null);
  const directSupport = useDirectSupportSwitch(setSupportTarget);
  const invitation = useAdminInvitationResend();

  const { data, isLoading, error } = useQuery({
    queryKey: workspaceKeys.list({ page, pageSize: PAGE_SIZE }),
    queryFn: () => api.workspaces.list({ page, pageSize: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const onError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.detail ?? e.title);
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });

  const create = useMutation({
    mutationFn: (body: CreateWorkspaceRequest) => api.workspaces.create(body),
    onSuccess: async (ws) => {
      await refresh();
      setCreateOpen(false);
      toast.success(t(ws.admins.length > 0 ? 'createdInvited' : 'created', { name: ws.name }));
    },
    onError,
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? api.workspaces.activate(id) : api.workspaces.deactivate(id),
    onSuccess: async (ws) => {
      await refresh();
      toast.success(ws.isActive ? 'Workspace freigegeben' : 'Workspace gesperrt');
    },
    onError,
  });

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Plattform"
        title="Alle"
        titleAccent="Workspaces"
        description="Kunden-Workspaces anlegen, Administratoren einladen und Workspaces sperren. Kundendaten bleiben dabei unsichtbar."
        actions={
          <Button
            variant="brand"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={() => setCreateOpen(true)}
          >
            Workspace anlegen
          </Button>
        }
      />

      {error ? (
        <EmptyState
          icon="Error"
          title="Die Workspaces konnten nicht geladen werden"
          description="Bitte laden Sie die Seite neu. Bleibt es dabei, ist der Dienst gerade nicht erreichbar."
        />
      ) : !isLoading && items.length === 0 ? (
        <EmptyState
          icon="Building"
          title="Noch keine Workspaces"
          description={t('emptyDescription')}
          action={{
            label: 'Workspace anlegen',
            leftIcon: <Plus size={14} aria-hidden />,
            onClick: () => setCreateOpen(true),
          }}
        />
      ) : (
        <WorkspacesList
          workspaces={items}
          busyId={setActive.isPending ? (setActive.variables?.id ?? null) : null}
          onToggleActive={(ws) => setActive.mutate({ id: ws.id, active: !ws.isActive })}
          onSupport={directSupport.pick}
          supportPending={directSupport.pending}
          invitation={invitation}
        />
      )}

      {data && data.total > PAGE_SIZE && (
        <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} />
      )}

      <WorkspaceCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        loading={create.isPending}
        onSubmit={(values) => create.mutate(values)}
      />
      <SupportStartDialog target={supportTarget} onClose={() => setSupportTarget(null)} />
    </>
  );
}
